import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { API } from '../../utils/api';
import 'react-quill/dist/quill.snow.css';
import ConfirmModal from '../../components/modals/ConfirmModal';
import ErrorModal from '../../components/modals/ErrorModal';
import SuccessModal from '../../components/modals/SuccessModal';
import PromptModal from '../../components/modals/PromptModal';

// Strict DOMPurify config for displaying inbound email HTML. We strip every
// script-execution vector before rendering: <script>, on* event handlers,
// javascript: hrefs, <iframe>, <object>, <embed>, <form>, <link>, <style>, etc.
// The contents come from external senders, so this is a real attack surface.
const sanitiseInboundEmailHtml = (html) =>
  DOMPurify.sanitize(html || '', {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input',
                  'button', 'textarea', 'select', 'option', 'link', 'meta',
                  'base', 'style'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus',
                  'onblur', 'onsubmit', 'onchange', 'srcdoc', 'sandbox',
                  'formaction', 'action'],
  });

const TABS = [
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'templates', label: 'Templates' },
  { key: 'inbox', label: 'Inbox' },
  { key: 'lead-sources', label: 'Lead Sources' },
];

// ============================================================================
// DIALOG HOOK — replaces window.alert / confirm / prompt with in-app modals.
// ============================================================================
//
// Usage:
//   const dialogs = useDialogs();
//   if (await dialogs.confirm({ title, message, variant: 'danger' })) { ... }
//   const value = await dialogs.prompt({ title, defaultValue: '...' });
//   dialogs.error('Something went wrong');
//   dialogs.success('Saved');
//   // and render: {dialogs.render()} somewhere in the JSX tree.
//

// Coerce any error detail (string, array of FastAPI validation errors, object) into a readable string.
const formatErrorDetail = (detail) => {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // FastAPI 422 returns [{loc, msg, type}, ...]
    return detail
      .map(d => {
        if (typeof d === 'string') return d;
        const loc = Array.isArray(d?.loc) ? d.loc.filter(x => x !== 'body').join('.') : '';
        const msg = d?.msg || d?.message || JSON.stringify(d);
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join('\n');
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return String(detail);
};

const useDialogs = () => {
  const [state, setState] = useState(null); // { kind, props, resolver }

  const open = useCallback((kind, props = {}) => {
    return new Promise(resolve => {
      setState({ kind, props, resolver: resolve });
    });
  }, []);

  const close = (val) => {
    setState(prev => {
      if (prev?.resolver) prev.resolver(val);
      return null;
    });
  };

  const dialogs = {
    confirm: (opts) => open('confirm', opts),
    prompt: (opts) => open('prompt', opts),
    error: (message, title = 'Error') => open('error', { title, message: formatErrorDetail(message) || 'An unexpected error occurred.' }),
    success: (message, title = 'Success') => open('success', { title, message: typeof message === 'string' ? message : formatErrorDetail(message) }),
    render: () => {
      if (!state) return null;
      if (state.kind === 'confirm') {
        return (
          <ConfirmModal
            isOpen
            onClose={() => close(false)}
            onConfirm={() => close(true)}
            {...state.props}
          />
        );
      }
      if (state.kind === 'prompt') {
        return (
          <PromptModal
            isOpen
            onClose={() => close(null)}
            onSubmit={(v) => close(v)}
            {...state.props}
          />
        );
      }
      if (state.kind === 'error') {
        return <ErrorModal isOpen onClose={() => close()} {...state.props} />;
      }
      if (state.kind === 'success') {
        return <SuccessModal isOpen onClose={() => close()} {...state.props} />;
      }
      return null;
    },
  };

  return dialogs;
};

// ============================================================================
// GROUPS — shared utilities
// ============================================================================

const useGroups = () => {
  const [groups, setGroups] = useState([]);
  const [ungroupedActiveCount, setUngroupedActiveCount] = useState(0);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/super-admin/outbound/groups`);
      setGroups(res.data.groups || []);
      setUngroupedActiveCount(res.data.ungrouped_active_count || 0);
    } catch (err) {
      console.error('Failed to fetch groups:', err);
    }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  return { groups, ungroupedActiveCount, refetchGroups: fetchGroups };
};

/**
 * GroupSelector — dropdown of existing groups with an inline "+ Create new" option.
 * Controlled via { groupId, newGroupName } state pair set by the parent.
 *
 * Pass `includeNone={true}` to show a "No group" choice (default true for assigning).
 */
const GroupSelector = ({ groups, groupId, newGroupName, onChange, includeNone = true, testId = 'group-selector', placeholder = 'No group' }) => {
  const isCreating = groupId === '__new__';
  return (
    <div className="space-y-2">
      <select
        data-testid={testId}
        value={isCreating ? '__new__' : (groupId || '')}
        onChange={e => {
          const v = e.target.value;
          if (v === '__new__') {
            onChange({ groupId: '__new__', newGroupName: newGroupName || '' });
          } else {
            onChange({ groupId: v || '', newGroupName: '' });
          }
        }}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
      >
        {includeNone && <option value="">{placeholder}</option>}
        {groups.map(g => (
          <option key={g.id} value={g.id}>{g.name} ({g.active_member_count} active)</option>
        ))}
        <option value="__new__">+ Create new group...</option>
      </select>
      {isCreating && (
        <input
          data-testid={`${testId}-new-name`}
          autoFocus
          placeholder="New group name (e.g. Phase 2)"
          value={newGroupName}
          onChange={e => onChange({ groupId: '__new__', newGroupName: e.target.value })}
          className="w-full px-3 py-2 border border-purple-300 bg-purple-50 rounded-lg text-sm"
        />
      )}
    </div>
  );
};

const GroupBadge = ({ group, className = '' }) => {
  if (!group) return null;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${className}`}
      style={{ backgroundColor: `${group.color}1a`, color: group.color, borderColor: `${group.color}66` }}
    >
      {group.name}
    </span>
  );
};


// ============================================================================
// CONTACTS TAB
// ============================================================================

const ContactsTab = () => {
  const dialogs = useDialogs();
  const { groups, refetchGroups } = useGroups();
  const [contacts, setContacts] = useState([]);
  const [tags, setTags] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 0 });
  const [filters, setFilters] = useState({ search: '', status: '', tag: '', group_id: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadGroup, setUploadGroup] = useState({ groupId: '', newGroupName: '' });
  const [uploadStep, setUploadStep] = useState('configure'); // 'configure' | 'preview' | 'committing'
  const [uploadPreview, setUploadPreview] = useState(null);
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [showBulkMove, setShowBulkMove] = useState(false);
  const [bulkMoveTarget, setBulkMoveTarget] = useState({ groupId: '', newGroupName: '' });

  // Group delete dialog state
  const [deleteGroupTarget, setDeleteGroupTarget] = useState(null); // group being deleted
  const [deleteGroupAction, setDeleteGroupAction] = useState('reassign'); // "reassign" | "ungroup"
  const [deleteGroupReassignTo, setDeleteGroupReassignTo] = useState('');
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);

  const [formData, setFormData] = useState({
    name: '', email: '', company: '', phone: '', tags: '',
    group_id: '', new_group_name: '',
  });

  const groupById = React.useMemo(() => {
    const m = {};
    groups.forEach(g => { m[g.id] = g; });
    return m;
  }, [groups]);

  const fetchContacts = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 50 });
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.tag) params.append('tag', filters.tag);
      if (filters.group_id) params.append('group_id', filters.group_id);
      const res = await axios.get(`${API}/super-admin/outbound/contacts?${params}`);
      setContacts(res.data.contacts);
      setTags(res.data.tags || []);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(() => fetchContacts(1), 300);
    return () => clearTimeout(t);
  }, [filters, fetchContacts]);

  // Clear selection whenever filters change so we don't accidentally bulk-move stale selections.
  useEffect(() => {
    setSelectedIds(new Set());
    setSelectAllMatching(false);
  }, [filters]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setSelectAllMatching(false);
  };

  const togglePageSelect = () => {
    const allOnPage = contacts.every(c => selectedIds.has(c.id));
    if (allOnPage) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        contacts.forEach(c => next.delete(c.id));
        return next;
      });
      setSelectAllMatching(false);
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        contacts.forEach(c => next.add(c.id));
        return next;
      });
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectAllMatching(false);
  };

  const openBulkMove = () => {
    setBulkMoveTarget({ groupId: '', newGroupName: '' });
    setShowBulkMove(true);
  };

  const handleBulkMove = async () => {
    const payload = {};
    if (bulkMoveTarget.groupId === '__new__' && bulkMoveTarget.newGroupName?.trim()) {
      payload.new_group_name = bulkMoveTarget.newGroupName.trim();
    } else {
      // Empty / "none" / a real id all map to group_id; backend treats empty as ungroup.
      payload.group_id = bulkMoveTarget.groupId || null;
    }

    if (selectAllMatching) {
      payload.filters = {
        status: filters.status,
        tag: filters.tag,
        group_id: filters.group_id,
        search: filters.search,
      };
    } else {
      payload.contact_ids = Array.from(selectedIds);
    }

    try {
      const res = await axios.post(`${API}/super-admin/outbound/contacts/bulk-move-to-group`, payload);
      const dest = bulkMoveTarget.groupId === '' || !bulkMoveTarget.groupId ? 'no group' :
        (bulkMoveTarget.groupId === '__new__' ? `new group "${bulkMoveTarget.newGroupName}"` : 'selected group');
      setShowBulkMove(false);
      clearSelection();
      refetchGroups();
      fetchContacts(pagination.page);
      dialogs.success(`Moved ${res.data.moved} contact(s) to ${dest}.`, 'Done');
    } catch (err) {
      dialogs.error(err.response?.data?.detail || 'Bulk move failed');
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        phone: formData.phone,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      // Group payload — handle inline create.
      if (formData.group_id === '__new__' && formData.new_group_name?.trim()) {
        payload.new_group_name = formData.new_group_name.trim();
      } else {
        payload.group_id = formData.group_id || null;
      }
      if (editContact) {
        await axios.put(`${API}/super-admin/outbound/contacts/${editContact.id}`, payload);
      } else {
        await axios.post(`${API}/super-admin/outbound/contacts`, payload);
      }
      setShowForm(false);
      setEditContact(null);
      setFormData({ name: '', email: '', company: '', phone: '', tags: '', group_id: '', new_group_name: '' });
      refetchGroups();
      fetchContacts(1);
    } catch (err) {
      dialogs.error(err.response?.data?.detail || 'Failed to save contact');
    }
  };

  const handleDelete = async (id) => {
    const ok = await dialogs.confirm({ title: 'Delete contact?', message: 'This cannot be undone.', confirmText: 'Delete', variant: 'danger' });
    if (!ok) return;
    await axios.delete(`${API}/super-admin/outbound/contacts/${id}`);
    fetchContacts(pagination.page);
  };

  const buildUploadFormData = () => {
    const fd = new FormData();
    // Defensive: only append if it's actually a File-like object with a non-empty stream.
    // Some browsers leave a stale `e.target.files` after a cancelled picker that React still treats
    // as truthy. We re-read from the DOM input as a fallback to recover gracefully.
    let file = uploadFile;
    if (!(file instanceof File) || file.size === 0) {
      const inp = document.querySelector('[data-testid="upload-file-input"]');
      const domFile = inp?.files?.[0];
      if (domFile instanceof File && domFile.size > 0) {
        file = domFile;
      }
    }
    if (!(file instanceof File) || file.size === 0) {
      throw new Error('FILE_NOT_ATTACHED');
    }
    fd.append('file', file, file.name);
    if (uploadGroup.groupId === '__new__' && uploadGroup.newGroupName?.trim()) {
      fd.append('new_group_name', uploadGroup.newGroupName.trim());
    } else if (uploadGroup.groupId) {
      fd.append('group_id', uploadGroup.groupId);
    }
    return fd;
  };

  const handleUploadAnalyse = async () => {
    if (!uploadFile) return dialogs.error('Pick a CSV file first');
    if (uploadGroup.groupId === '__new__' && !uploadGroup.newGroupName?.trim()) {
      return dialogs.error('Enter a name for the new group');
    }
    try {
      const fd = buildUploadFormData();
      fd.append('preview', 'true');
      // Override the global Content-Type=application/json default so axios sets
      // the multipart boundary correctly. Setting to undefined lets axios+browser handle it.
      const res = await axios.post(`${API}/super-admin/outbound/contacts/upload`, fd, {
        headers: { 'Content-Type': undefined },
      });
      setUploadPreview(res.data);
      setUploadStep('preview');
    } catch (err) {
      if (err?.message === 'FILE_NOT_ATTACHED') {
        return dialogs.error('The selected file looks empty or was lost. Please click "Choose file" again and re-select your CSV.');
      }
      dialogs.error(err.response?.data?.detail || 'Could not analyse CSV');
    }
  };

  const handleUploadConfirm = async () => {
    if (!uploadFile) return;
    setUploadStep('committing');
    try {
      const res = await axios.post(`${API}/super-admin/outbound/contacts/upload`, buildUploadFormData(), {
        headers: { 'Content-Type': undefined },
      });
      setUploadResult(res.data);
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadGroup({ groupId: '', newGroupName: '' });
      setUploadPreview(null);
      setUploadStep('configure');
      refetchGroups();
      fetchContacts(1);
    } catch (err) {
      setUploadStep('preview');
      if (err?.message === 'FILE_NOT_ATTACHED') {
        return dialogs.error('The file is no longer available. Please go back and re-select your CSV.');
      }
      dialogs.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadGroup({ groupId: '', newGroupName: '' });
    setUploadPreview(null);
    setUploadStep('configure');
  };

  const openEdit = (c) => {
    setEditContact(c);
    setFormData({
      name: c.name,
      email: c.email,
      company: c.company,
      phone: c.phone || '',
      tags: (c.tags || []).join(', '),
      group_id: c.group_id || '',
      new_group_name: '',
    });
    setShowForm(true);
  };

  const handleDeleteGroup = async (g) => {
    // Empty groups → simple confirm. Populated groups → dedicated dialog with reassign/ungroup choices.
    if (g.member_count === 0) {
      const ok = await dialogs.confirm({
        title: 'Delete group?',
        message: `Delete group "${g.name}"? This cannot be undone.`,
        confirmText: 'Delete',
        variant: 'danger',
      });
      if (!ok) return;
      try {
        await axios.delete(`${API}/super-admin/outbound/groups/${g.id}`);
        refetchGroups();
        fetchContacts(pagination.page);
      } catch (err) {
        dialogs.error(err.response?.data?.detail || 'Failed to delete group');
      }
      return;
    }

    // Pre-select first viable reassign target (any other existing group), else fall back to ungroup.
    const otherGroups = groups.filter(o => o.id !== g.id);
    setDeleteGroupTarget(g);
    setDeleteGroupAction(otherGroups.length > 0 ? 'reassign' : 'ungroup');
    setDeleteGroupReassignTo(otherGroups[0]?.id || '');
  };

  const confirmDeleteGroup = async () => {
    if (!deleteGroupTarget) return;
    if (deleteGroupAction === 'reassign' && !deleteGroupReassignTo) {
      dialogs.error('Select a group to move members to');
      return;
    }
    setIsDeletingGroup(true);
    try {
      const url = deleteGroupAction === 'reassign'
        ? `${API}/super-admin/outbound/groups/${deleteGroupTarget.id}?reassign_to=${deleteGroupReassignTo}`
        : `${API}/super-admin/outbound/groups/${deleteGroupTarget.id}`;
      await axios.delete(url);
      const movedCount = deleteGroupTarget.member_count;
      const targetGroup = groups.find(g => g.id === deleteGroupReassignTo);
      const message = deleteGroupAction === 'reassign'
        ? `${movedCount} contact${movedCount === 1 ? '' : 's'} moved to "${targetGroup?.name || 'selected group'}".`
        : `${movedCount} contact${movedCount === 1 ? '' : 's'} are now ungrouped.`;
      setDeleteGroupTarget(null);
      refetchGroups();
      fetchContacts(pagination.page);
      dialogs.success(message, 'Group deleted');
    } catch (err) {
      dialogs.error(err.response?.data?.detail || 'Failed to delete group');
    } finally {
      setIsDeletingGroup(false);
    }
  };

  const handleRenameGroup = async (g) => {
    const newName = await dialogs.prompt({
      title: `Rename "${g.name}"`,
      message: 'Enter a new name for this group.',
      defaultValue: g.name,
      placeholder: 'Group name',
      confirmText: 'Rename',
      variant: 'purple',
    });
    if (!newName || newName === g.name) return;
    try {
      await axios.put(`${API}/super-admin/outbound/groups/${g.id}`, { name: newName });
      refetchGroups();
    } catch (err) {
      dialogs.error(err.response?.data?.detail || 'Failed to rename group');
    }
  };

  const handleCreateGroup = async () => {
    const name = await dialogs.prompt({
      title: 'New group',
      message: 'Give this group a clear, short name (e.g. "Phase 2", "VIP", "Builders 2026").',
      placeholder: 'Group name',
      confirmText: 'Create',
      variant: 'purple',
    });
    if (!name) return;
    try {
      await axios.post(`${API}/super-admin/outbound/groups`, { name });
      refetchGroups();
    } catch (err) {
      dialogs.error(err.response?.data?.detail || 'Failed to create group');
    }
  };

  return (
    <div className="space-y-4">
      {/* Actions bar */}
      <div className="flex flex-wrap items-center gap-3">
        <input data-testid="contacts-search" type="text" placeholder="Search contacts..." value={filters.search}
          onChange={e => setFilters(p => ({ ...p, search: e.target.value }))}
          className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
        <select data-testid="contacts-status-filter" value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value }))}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="unsubscribed">Unsubscribed</option>
        </select>
        {tags.length > 0 && (
          <select data-testid="contacts-tag-filter" value={filters.tag} onChange={e => setFilters(p => ({ ...p, tag: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="">All Tags</option>
            {tags.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        <select data-testid="contacts-group-filter" value={filters.group_id} onChange={e => setFilters(p => ({ ...p, group_id: e.target.value }))}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Groups</option>
          <option value="none">No group</option>
          {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <button data-testid="manage-groups-btn" onClick={() => setShowGroupManager(s => !s)}
          className="px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
          {showGroupManager ? 'Hide Groups' : 'Manage Groups'}
        </button>
        <button data-testid="add-contact-btn" onClick={() => { setEditContact(null); setFormData({ name: '', email: '', company: '', phone: '', tags: '', group_id: '', new_group_name: '' }); setShowForm(true); }}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors">
          Add Contact
        </button>
        <button data-testid="upload-csv-btn" onClick={() => { setUploadFile(null); setUploadGroup({ groupId: '', newGroupName: '' }); setUploadPreview(null); setUploadStep('configure'); setShowUploadModal(true); }}
          className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 cursor-pointer transition-colors">
          Upload CSV
        </button>
        <button onClick={async () => {
            try {
              const token = localStorage.getItem('token');
              const res = await axios.get(`${API}/super-admin/outbound/contacts/export`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
              });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement('a');
              a.href = url;
              a.download = 'outbound_contacts_export.csv';
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
            } catch (err) {
              dialogs.error('Export failed');
            }
          }} data-testid="export-contacts-btn"
          className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
          Export CSV
        </button>
      </div>

      {/* Group manager panel */}
      {showGroupManager && (
        <div data-testid="group-manager-panel" className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">Contact Groups</h4>
              <p className="text-xs text-gray-500">Each contact can belong to a single group. Use groups to target distinct cohorts in different campaigns.</p>
            </div>
            <button data-testid="create-group-btn" onClick={handleCreateGroup}
              className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700">
              + New Group
            </button>
          </div>
          {groups.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No groups yet — create one to get started.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {groups.map(g => {
                const a = g.analytics || { campaigns_sent: 0, emails_sent: 0, open_rate: 0, click_rate: 0, replies: 0 };
                return (
                  <div key={g.id} data-testid={`group-card-${g.id}`} className="flex flex-col gap-2 p-3 border border-gray-100 rounded-lg hover:border-gray-200 hover:shadow-sm transition-all">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1 space-y-1">
                        <GroupBadge group={g} />
                        <p className="text-xs text-gray-500">{g.active_member_count} active / {g.member_count} total</p>
                      </div>
                      <div className="flex flex-col items-end gap-0.5">
                        <button onClick={() => handleRenameGroup(g)} className="text-xs text-purple-600 hover:text-purple-800 px-1">Rename</button>
                        <button onClick={() => handleDeleteGroup(g)} className="text-xs text-red-500 hover:text-red-700 px-1">Delete</button>
                      </div>
                    </div>

                    {/* Analytics rollup */}
                    {a.campaigns_sent > 0 ? (
                      <div className="grid grid-cols-4 gap-2 pt-2 border-t border-gray-100" data-testid={`group-analytics-${g.id}`}>
                        <div className="text-center">
                          <p className="text-sm font-semibold text-gray-900 leading-tight">{a.campaigns_sent}</p>
                          <p className="text-[10px] text-gray-500 uppercase tracking-wide">Campaigns</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-semibold text-blue-600 leading-tight">{a.open_rate}%</p>
                          <p className="text-[10px] text-gray-500 uppercase tracking-wide">Open</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-semibold text-amber-600 leading-tight">{a.click_rate}%</p>
                          <p className="text-[10px] text-gray-500 uppercase tracking-wide">Click</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-semibold text-green-600 leading-tight">{a.replies}</p>
                          <p className="text-[10px] text-gray-500 uppercase tracking-wide">Replies</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-[11px] text-gray-400 italic pt-1 border-t border-gray-100">No campaigns sent yet</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Upload result */}
      {uploadResult && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
          <p className="font-medium text-green-700">Upload complete: {uploadResult.created} contacts processed, {uploadResult.skipped} skipped</p>
          {uploadResult.errors?.length > 0 && (
            <ul className="mt-1 text-red-600">{uploadResult.errors.slice(0, 5).map((e, i) => (
              <li key={i}>{typeof e === 'string' ? e : JSON.stringify(e)}</li>
            ))}</ul>
          )}
          <button onClick={() => setUploadResult(null)} className="mt-1 text-xs text-gray-500 underline">Dismiss</button>
        </div>
      )}

      {/* Bulk selection action bar */}
      {selectedIds.size > 0 && (
        <div data-testid="bulk-action-bar" className="flex flex-wrap items-center justify-between gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-purple-900">
              {selectAllMatching
                ? `All ${pagination.total} matching contact${pagination.total === 1 ? '' : 's'} selected`
                : `${selectedIds.size} contact${selectedIds.size === 1 ? '' : 's'} selected`}
            </span>
            {!selectAllMatching && pagination.total > contacts.length && contacts.every(c => selectedIds.has(c.id)) && (
              <button data-testid="select-all-matching-btn" onClick={() => setSelectAllMatching(true)}
                className="text-xs text-purple-700 underline hover:text-purple-900">
                Select all {pagination.total} matching the filter
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="bulk-move-btn" onClick={openBulkMove}
              className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700">
              Move to group...
            </button>
            <button onClick={clearSelection}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-white">
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Bulk move modal */}
      {showBulkMove && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md" data-testid="bulk-move-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-1">
              Move {selectAllMatching ? pagination.total : selectedIds.size} contact(s) to group
            </h3>
            <p className="text-xs text-gray-500 mb-4">Each contact can only belong to one group; this overwrites any existing group assignment.</p>
            <GroupSelector
              groups={groups}
              groupId={bulkMoveTarget.groupId}
              newGroupName={bulkMoveTarget.newGroupName}
              onChange={({ groupId, newGroupName }) => setBulkMoveTarget({ groupId, newGroupName })}
              testId="bulk-move-group-selector"
              placeholder="No group (ungroup these contacts)"
            />
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowBulkMove(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button data-testid="confirm-bulk-move-btn" onClick={handleBulkMove}
                disabled={bulkMoveTarget.groupId === '__new__' && !bulkMoveTarget.newGroupName?.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50">
                Move
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete group modal — surfaces the consequence and lets the user pick reassign vs ungroup */}
      {deleteGroupTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md" data-testid="delete-group-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Delete group "{deleteGroupTarget.name}"?</h3>
            <p className="text-sm text-gray-600 mb-4">
              This group has <strong>{deleteGroupTarget.member_count}</strong> member{deleteGroupTarget.member_count === 1 ? '' : 's'}.
              Choose what happens to them:
            </p>

            <div className="space-y-3">
              {/* Reassign option — only shown if there are other groups */}
              {groups.filter(o => o.id !== deleteGroupTarget.id).length > 0 && (
                <label
                  data-testid="delete-action-reassign"
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    deleteGroupAction === 'reassign' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input type="radio" name="delete-action" value="reassign"
                    checked={deleteGroupAction === 'reassign'}
                    onChange={() => setDeleteGroupAction('reassign')}
                    className="mt-1" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Move to another group</p>
                    <p className="text-xs text-gray-500 mb-2">All members move to the group you select below.</p>
                    {deleteGroupAction === 'reassign' && (
                      <select
                        data-testid="delete-reassign-select"
                        value={deleteGroupReassignTo}
                        onChange={e => setDeleteGroupReassignTo(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      >
                        {groups.filter(o => o.id !== deleteGroupTarget.id).map(o => (
                          <option key={o.id} value={o.id}>{o.name} ({o.active_member_count} active)</option>
                        ))}
                      </select>
                    )}
                  </div>
                </label>
              )}

              <label
                data-testid="delete-action-ungroup"
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  deleteGroupAction === 'ungroup' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input type="radio" name="delete-action" value="ungroup"
                  checked={deleteGroupAction === 'ungroup'}
                  onChange={() => setDeleteGroupAction('ungroup')}
                  className="mt-1" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Ungroup members</p>
                  <p className="text-xs text-gray-500">Members stay as contacts but won't belong to any group. They can still be targeted via "All active contacts" in campaigns.</p>
                </div>
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setDeleteGroupTarget(null)} disabled={isDeletingGroup}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button data-testid="confirm-delete-group-btn" onClick={confirmDeleteGroup} disabled={isDeletingGroup}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
                {isDeletingGroup ? 'Deleting...' : 'Delete group'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload CSV modal — two-step wizard: configure -> preview & confirm */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md" data-testid="upload-csv-modal">

            {uploadStep === 'configure' && (
              <>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Upload Contacts CSV</h3>
                <p className="text-xs text-gray-500 mb-4">Expected columns: <code className="bg-gray-100 px-1 py-0.5 rounded">name, email, company, phone, tags</code>. Email is required.</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Assign all imported contacts to group (optional)</label>
                    <GroupSelector
                      groups={groups}
                      groupId={uploadGroup.groupId}
                      newGroupName={uploadGroup.newGroupName}
                      onChange={({ groupId, newGroupName }) => setUploadGroup({ groupId, newGroupName })}
                      testId="upload-group-selector"
                      placeholder="No group (leave ungrouped)"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">CSV file</label>
                    <input data-testid="upload-file-input" type="file" accept=".csv"
                      onChange={e => setUploadFile(e.target.files[0])}
                      className="w-full text-sm text-gray-600" />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-5">
                  <button onClick={closeUploadModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button data-testid="analyse-upload-btn" onClick={handleUploadAnalyse} disabled={!uploadFile}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50">
                    Next: Review
                  </button>
                </div>
              </>
            )}

            {uploadStep === 'preview' && uploadPreview && (
              <>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Confirm import</h3>
                <p className="text-xs text-gray-500 mb-4">
                  Reviewing <span className="font-medium text-gray-700">{uploadPreview.filename}</span> — nothing has been saved yet.
                </p>

                {/* Headline counts */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center" data-testid="preview-new-count">
                    <p className="text-2xl font-bold text-green-700">{uploadPreview.new_contacts}</p>
                    <p className="text-xs text-green-700">New contacts</p>
                  </div>
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-center" data-testid="preview-existing-count">
                    <p className="text-2xl font-bold text-blue-700">{uploadPreview.existing_contacts}</p>
                    <p className="text-xs text-blue-700">Already on file</p>
                  </div>
                </div>

                <ul className="text-sm space-y-1 mb-4 text-gray-700">
                  <li className="flex justify-between"><span>Total rows in file:</span><span className="font-medium">{uploadPreview.total_rows}</span></li>
                  <li className="flex justify-between"><span>Valid (will be processed):</span><span className="font-medium text-gray-900">{uploadPreview.valid_rows}</span></li>
                  {uploadPreview.duplicates_in_file > 0 && (
                    <li className="flex justify-between text-amber-700"><span>Duplicate emails inside file:</span><span className="font-medium">{uploadPreview.duplicates_in_file}</span></li>
                  )}
                  {uploadPreview.skipped > 0 && (
                    <li className="flex justify-between text-red-600"><span>Skipped (invalid email):</span><span className="font-medium">{uploadPreview.skipped}</span></li>
                  )}
                  {uploadPreview.target_group_name && (
                    <li className="flex justify-between pt-2 border-t border-gray-100 mt-2">
                      <span>Group:</span>
                      <span className="font-medium text-purple-700">{uploadPreview.target_group_name}</span>
                    </li>
                  )}
                </ul>

                {uploadPreview.errors?.length > 0 && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg max-h-32 overflow-y-auto">
                    <p className="text-xs font-medium text-red-700 mb-1">First {uploadPreview.errors.length} skipped row(s):</p>
                    <ul className="text-xs text-red-600 space-y-0.5">
                      {uploadPreview.errors.slice(0, 10).map((e, i) => (
                        <li key={i}>{typeof e === 'string' ? e : JSON.stringify(e)}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {uploadPreview.existing_contacts > 0 && (
                  <p className="text-xs text-gray-500 italic mb-3">
                    Existing contacts won't be duplicated — their group assignment will be updated to match this import.
                  </p>
                )}

                <div className="flex justify-end gap-3 mt-2">
                  <button data-testid="back-to-configure-btn" onClick={() => { setUploadStep('configure'); setUploadPreview(null); }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Back</button>
                  <button data-testid="confirm-upload-btn" onClick={handleUploadConfirm}
                    disabled={uploadPreview.valid_rows === 0}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50">
                    Confirm — Import {uploadPreview.valid_rows} contact{uploadPreview.valid_rows === 1 ? '' : 's'}
                  </button>
                </div>
              </>
            )}

            {uploadStep === 'committing' && (
              <div className="py-8 text-center">
                <div className="inline-block w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-3"></div>
                <p className="text-sm font-medium text-gray-900">Importing {uploadPreview?.valid_rows || ''} contacts...</p>
                <p className="text-xs text-gray-500 mt-1">This may take a moment for large files.</p>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Contact form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md" data-testid="contact-form-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{editContact ? 'Edit Contact' : 'Add Contact'}</h3>
            <div className="space-y-3">
              <input placeholder="Name" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <input placeholder="Email *" type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                disabled={!!editContact}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100" />
              <input placeholder="Company" value={formData.company} onChange={e => setFormData(p => ({ ...p, company: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <input placeholder="Phone" value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <input placeholder="Tags (comma separated)" value={formData.tags} onChange={e => setFormData(p => ({ ...p, tags: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Group</label>
                <GroupSelector
                  groups={groups}
                  groupId={formData.group_id}
                  newGroupName={formData.new_group_name}
                  onChange={({ groupId, newGroupName }) => setFormData(p => ({ ...p, group_id: groupId, new_group_name: newGroupName }))}
                  testId="contact-group-selector"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => { setShowForm(false); setEditContact(null); }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button data-testid="save-contact-btn" onClick={handleSave}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Contacts table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" /></div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="font-medium">No contacts yet</p>
            <p className="text-sm mt-1">Add contacts manually or upload a CSV file</p>
          </div>
        ) : (
          <>
            <table data-testid="contacts-table" className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input data-testid="select-all-page-checkbox" type="checkbox"
                      checked={contacts.length > 0 && contacts.every(c => selectedIds.has(c.id))}
                      onChange={togglePageSelect}
                      className="rounded" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tags</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {contacts.map(c => (
                  <tr key={c.id} className={`hover:bg-gray-50 ${selectedIds.has(c.id) ? 'bg-purple-50/50' : ''}`}>
                    <td className="px-3 py-2.5">
                      <input data-testid={`select-contact-${c.id}`} type="checkbox"
                        checked={selectedIds.has(c.id)}
                        onChange={() => toggleSelect(c.id)}
                        className="rounded" />
                    </td>
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{c.name || '-'}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">{c.email}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">{c.company || '-'}</td>
                    <td className="px-4 py-2.5 text-sm">
                      {c.group_id && groupById[c.group_id]
                        ? <GroupBadge group={groupById[c.group_id]} />
                        : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-2.5">{(c.tags || []).map(t => (
                      <span key={t} className="inline-block px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full mr-1">{t}</span>
                    ))}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${c.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => openEdit(c)} className="text-sm text-purple-600 hover:text-purple-800 mr-3">Edit</button>
                      <button onClick={() => handleDelete(c.id)} className="text-sm text-red-500 hover:text-red-700">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pagination.total_pages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                <span className="text-sm text-gray-700">{pagination.total} contacts</span>
                <div className="flex gap-2">
                  <button onClick={() => fetchContacts(pagination.page - 1)} disabled={pagination.page <= 1}
                    className="px-3 py-1 rounded-lg text-sm border border-gray-300 disabled:opacity-40">Prev</button>
                  <span className="text-sm text-gray-700 py-1">Page {pagination.page} of {pagination.total_pages}</span>
                  <button onClick={() => fetchContacts(pagination.page + 1)} disabled={pagination.page >= pagination.total_pages}
                    className="px-3 py-1 rounded-lg text-sm border border-gray-300 disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {dialogs.render()}
    </div>
  );
};


// ============================================================================
// TEMPLATES TAB
// ============================================================================

const PERSONALISATION_VARS = [
  { var: '{contact_name}', label: 'Contact Name' },
  { var: '{company_name}', label: 'Company Name' },
  { var: '{your_name}', label: 'Your Name' },
  { var: '{your_company}', label: 'Your Company' },
];

const CATEGORIES = ['initial_contact', 'follow_up', 'general'];

const TemplatesTab = () => {
  const dialogs = useDialogs();
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [formData, setFormData] = useState({ name: '', subject: '', body_html: '', category: 'general' });
  const [ReactQuill, setReactQuill] = useState(null);

  // Preview modal state
  const [previewData, setPreviewData] = useState(null);
  const [previewMode, setPreviewMode] = useState('html'); // 'html' | 'plain'
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    import('react-quill').then(mod => setReactQuill(() => mod.default));
  }, []);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/super-admin/outbound/templates`);
      setTemplates(res.data.templates);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleSave = async () => {
    try {
      if (editTemplate) {
        await axios.put(`${API}/super-admin/outbound/templates/${editTemplate.id}`, formData);
      } else {
        await axios.post(`${API}/super-admin/outbound/templates`, formData);
      }
      setShowEditor(false);
      setEditTemplate(null);
      setFormData({ name: '', subject: '', body_html: '', category: 'general' });
      fetchTemplates();
    } catch (err) {
      dialogs.error(err.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDelete = async (id) => {
    const ok = await dialogs.confirm({
      title: 'Delete template?',
      message: 'This template will be permanently removed. Existing campaigns using it will keep their copies.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    await axios.delete(`${API}/super-admin/outbound/templates/${id}`);
    fetchTemplates();
  };

  const openEdit = (t) => {
    setEditTemplate(t);
    setFormData({ name: t.name, subject: t.subject, body_html: t.body_html, category: t.category || 'general' });
    setShowEditor(true);
  };

  /**
   * Render the email exactly as a recipient would see it.
   * Uses the same `_build_email_html`/`_build_plain_text` pipeline as a real send,
   * so what you see here is what they get.
   *
   * @param savedTemplate - the saved template document, OR null to preview the
   *                       in-editor unsaved formData.
   */
  const openPreview = async (savedTemplate) => {
    try {
      setPreviewLoading(true);
      setPreviewMode('html');

      const isUnsaved = !savedTemplate;
      const id = isUnsaved ? '_unsaved_' : savedTemplate.id;
      const body = isUnsaved
        ? { subject: formData.subject, body_html: formData.body_html }
        : null;

      const res = await axios.post(
        `${API}/super-admin/outbound/templates/${id}/preview`,
        body || {}
      );
      setPreviewData(res.data);
    } catch (err) {
      console.error('Failed to render preview', err);
      await dialogs.alert({
        title: 'Preview failed',
        message: err?.response?.data?.detail || 'Could not render preview. Please save the template first.',
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewData(null);
    setPreviewMode('html');
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">Create reusable email templates for your campaigns</p>
        <button data-testid="add-template-btn" onClick={() => { setEditTemplate(null); setFormData({ name: '', subject: '', body_html: '', category: 'general' }); setShowEditor(true); }}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors">
          New Template
        </button>
      </div>

      {/* Template editor modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-editor-modal">
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-bold text-gray-900">{editTemplate ? 'Edit Template' : 'New Template'}</h3>

              {/* Personalisation vars */}
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-gray-500 py-1">Variables:</span>
                {PERSONALISATION_VARS.map(v => (
                  <button key={v.var} onClick={() => {
                    navigator.clipboard.writeText(v.var);
                  }} className="text-xs px-2 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded hover:bg-purple-100" title={`Click to copy: ${v.var}`}>
                    {v.label}: <code>{v.var}</code>
                  </button>
                ))}
              </div>

              <input data-testid="template-name-input" placeholder="Template name" value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />

              <select value={formData.category} onChange={e => setFormData(p => ({ ...p, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
              </select>

              <input data-testid="template-subject-input" placeholder="Subject line (supports {contact_name}, {company_name}...)" value={formData.subject}
                onChange={e => setFormData(p => ({ ...p, subject: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />

              <div data-testid="template-body-editor" className="border rounded-lg overflow-hidden">
                {ReactQuill ? (
                  <ReactQuill
                    theme="snow"
                    value={formData.body_html}
                    onChange={val => setFormData(p => ({ ...p, body_html: val }))}
                    modules={quillModules}
                    style={{ minHeight: '300px' }}
                  />
                ) : (
                  <div className="p-4 text-gray-400 text-sm">Loading editor...</div>
                )}
              </div>

              <div className="flex justify-between items-center gap-3">
                <button
                  data-testid="preview-unsaved-template-btn"
                  type="button"
                  onClick={() => openPreview(null)}
                  disabled={previewLoading || !formData.body_html}
                  className="px-4 py-2 border border-purple-300 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  title="See how this template will render in an email client"
                >
                  <span aria-hidden="true">👁</span>
                  {previewLoading ? 'Rendering…' : 'Preview email'}
                </button>
                <div className="flex gap-3">
                  <button onClick={() => { setShowEditor(false); setEditTemplate(null); }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button data-testid="save-template-btn" onClick={handleSave}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">Save Template</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Templates list */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-full flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" /></div>
        ) : templates.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            <p className="font-medium">No templates yet</p>
            <p className="text-sm mt-1">Create your first email template to use in campaigns</p>
          </div>
        ) : templates.map(t => (
          <div key={t.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-2">
              <h4 className="font-semibold text-gray-900 text-sm">{t.name}</h4>
              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">{(t.category || 'general').replace('_', ' ')}</span>
            </div>
            <p className="text-xs text-gray-500 mb-3 truncate">Subject: {t.subject}</p>
            <div className="text-xs text-gray-400 mb-3 h-12 overflow-hidden" dangerouslySetInnerHTML={{ __html: sanitiseInboundEmailHtml(t.body_html?.substring(0, 200)) }} />
            <div className="flex gap-3 pt-2 border-t border-gray-100">
              <button
                data-testid={`preview-template-btn-${t.id}`}
                onClick={() => openPreview(t)}
                disabled={previewLoading}
                className="text-xs text-emerald-600 hover:text-emerald-800 font-medium disabled:opacity-50"
              >
                Preview
              </button>
              <button onClick={() => openEdit(t)} className="text-xs text-purple-600 hover:text-purple-800 font-medium">Edit</button>
              <button onClick={() => handleDelete(t.id)} className="text-xs text-red-500 hover:text-red-700 font-medium">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Preview modal — renders the email via the same pipeline as a real send */}
      {previewData && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
          onClick={closePreview}
          data-testid="template-preview-modal"
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-bold text-gray-900">Email Preview</h3>
                  <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Sample data</span>
                </div>
                <p className="text-xs text-gray-500">
                  Personalisation tokens shown using sample values
                  (<code className="px-1 bg-gray-100 rounded">John Smith</code> /
                  {' '}<code className="px-1 bg-gray-100 rounded">Acme Construction Ltd</code>).
                </p>
              </div>
              <button
                onClick={closePreview}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none px-2"
                aria-label="Close preview"
                data-testid="close-preview-btn"
              >
                ×
              </button>
            </div>

            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
              <div className="text-xs text-gray-500 mb-1">Subject</div>
              <div className="text-sm font-medium text-gray-900 truncate" data-testid="preview-subject">
                {previewData.subject || <span className="text-gray-400 italic">(empty)</span>}
              </div>
            </div>

            <div className="px-6 pt-3 border-b border-gray-200 flex gap-1">
              <button
                onClick={() => setPreviewMode('html')}
                className={`px-3 py-1.5 text-xs font-medium border-b-2 ${
                  previewMode === 'html'
                    ? 'text-purple-700 border-purple-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
                data-testid="preview-mode-html"
              >
                HTML version
              </button>
              <button
                onClick={() => setPreviewMode('plain')}
                className={`px-3 py-1.5 text-xs font-medium border-b-2 ${
                  previewMode === 'plain'
                    ? 'text-purple-700 border-purple-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
                data-testid="preview-mode-plain"
              >
                Plain text version
              </button>
            </div>

            <div className="flex-1 overflow-auto bg-gray-100 p-4">
              {previewMode === 'html' ? (
                <iframe
                  title="email-preview-html"
                  srcDoc={previewData.html}
                  sandbox=""
                  className="w-full h-full min-h-[480px] bg-white rounded shadow-sm border border-gray-200"
                  data-testid="preview-iframe-html"
                />
              ) : (
                <pre
                  className="w-full h-full min-h-[480px] bg-white rounded shadow-sm border border-gray-200 p-6 text-sm font-mono text-gray-800 whitespace-pre-wrap overflow-auto"
                  data-testid="preview-iframe-plain"
                >
                  {previewData.plain_text || '(empty)'}
                </pre>
              )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200 text-xs text-gray-500 flex justify-between items-center">
              <span>This is exactly what recipients will see — same render pipeline as a real send.</span>
              <button
                onClick={closePreview}
                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {dialogs.render()}
    </div>
  );
};


// ============================================================================
// CAMPAIGNS TAB
// ============================================================================

const CampaignsTab = () => {
  const dialogs = useDialogs();
  const { groups } = useGroups();
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [activeContactCount, setActiveContactCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState(null); // null => create mode
  const [editingStatus, setEditingStatus] = useState(null);          // status of campaign being edited
  const [editingHasSends, setEditingHasSends] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetail, setCampaignDetail] = useState(null);

  const isEditMode = !!editingCampaignId;
  const recipientsLocked = isEditMode && editingHasSends;
  const structuralLocked = isEditMode && editingStatus === 'active';

  const blankForm = () => ({
    name: '', sender_name: 'SiteOrbit',
    steps: [
      { step_number: 1, template_id: '', delay_days: 0 },
      { step_number: 2, template_id: '', delay_days: 5 },
      { step_number: 3, template_id: '', delay_days: 12 },
    ],
    use_all_contacts: true, group_id: '', contact_ids: [],
    start_date: '',
  });

  const [form, setForm] = useState(blankForm());

  const groupById = React.useMemo(() => {
    const m = {};
    groups.forEach(g => { m[g.id] = g; });
    return m;
  }, [groups]);

  const fetchCampaigns = async () => {
    setIsLoading(true);
    try {
      const [campRes, tplRes, activeCountRes] = await Promise.all([
        axios.get(`${API}/super-admin/outbound/campaigns`),
        axios.get(`${API}/super-admin/outbound/templates`),
        axios.get(`${API}/super-admin/outbound/contacts?status=active&page_size=1`),
      ]);
      setCampaigns(campRes.data.campaigns);
      setTemplates(tplRes.data.templates);
      setActiveContactCount(activeCountRes.data.pagination?.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchCampaigns(); }, []);

  const openCreate = () => {
    setEditingCampaignId(null);
    setEditingStatus(null);
    setEditingHasSends(false);
    setForm(blankForm());
    setShowBuilder(true);
  };

  const openEdit = async (c) => {
    // Pull tracking to know if recipients can be edited.
    let hasSends = false;
    try {
      const res = await axios.get(`${API}/super-admin/outbound/campaigns/${c.id}`);
      hasSends = (res.data.sends || []).length > 0;
    } catch (err) {
      console.error(err);
    }

    // Build the form steps; pad to 3 rows for UX consistency.
    const steps = [...(c.steps || [])];
    while (steps.length < 3) {
      steps.push({ step_number: steps.length + 1, template_id: '', delay_days: steps.length * 5 });
    }

    // Convert ISO start_date to datetime-local input format (in browser TZ)
    let startLocal = '';
    if (c.start_date) {
      const d = new Date(c.start_date);
      const pad = n => String(n).padStart(2, '0');
      startLocal = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }

    setEditingCampaignId(c.id);
    setEditingStatus(c.status);
    setEditingHasSends(hasSends);
    setForm({
      name: c.name || '',
      sender_name: c.sender_name || 'SiteOrbit',
      steps,
      use_all_contacts: c.use_all_contacts !== undefined ? c.use_all_contacts : !c.group_id,
      group_id: c.group_id || '',
      contact_ids: c.contact_ids || [],
      start_date: startLocal,
    });
    setShowBuilder(true);
  };

  const buildPayload = ({ asDraft = false } = {}) => {
    const startIso = form.start_date ? new Date(form.start_date).toISOString() : '';
    const payload = {
      name: form.name,
      sender_name: form.sender_name,
      start_date: startIso,
      steps: form.steps.filter(s => s.template_id),
      use_all_contacts: form.use_all_contacts,
      group_id: form.use_all_contacts ? null : (form.group_id || null),
    };
    if (asDraft) payload.as_draft = true;
    return payload;
  };

  const handleSaveDraft = async () => {
    try {
      if (!form.name) return dialogs.error('Campaign name is required');
      const payload = buildPayload({ asDraft: true });
      if (isEditMode) {
        await axios.put(`${API}/super-admin/outbound/campaigns/${editingCampaignId}`, payload);
      } else {
        await axios.post(`${API}/super-admin/outbound/campaigns`, payload);
      }
      setShowBuilder(false);
      fetchCampaigns();
    } catch (err) {
      dialogs.error(err.response?.data?.detail || 'Failed to save draft');
    }
  };

  const handleLaunch = async () => {
    try {
      if (!form.name) return dialogs.error('Campaign name is required');
      const payload = buildPayload({ asDraft: false });
      if (payload.steps.length === 0) return dialogs.error('Select at least one template');
      if (!payload.use_all_contacts && !payload.group_id) return dialogs.error('Pick a group or choose "All active contacts"');

      if (isEditMode) {
        // 1) Persist edits  2) Promote to active.
        await axios.put(`${API}/super-admin/outbound/campaigns/${editingCampaignId}`, payload);
        await axios.put(`${API}/super-admin/outbound/campaigns/${editingCampaignId}/status`, { status: 'active' });
      } else {
        // Single-shot create that goes straight to active/scheduled.
        await axios.post(`${API}/super-admin/outbound/campaigns`, payload);
      }
      setShowBuilder(false);
      fetchCampaigns();
    } catch (err) {
      dialogs.error(err.response?.data?.detail || 'Failed to launch campaign');
    }
  };

  const handleSaveChanges = async () => {
    // Edit-only path that doesn't change status (used for paused / scheduled / active).
    try {
      if (!form.name) return dialogs.error('Campaign name is required');
      const payload = buildPayload({ asDraft: false });
      // Remove fields the backend will reject when active
      if (structuralLocked) {
        delete payload.steps;
        delete payload.start_date;
        delete payload.group_id;
        delete payload.use_all_contacts;
      }
      if (recipientsLocked) {
        delete payload.group_id;
        delete payload.use_all_contacts;
      }
      await axios.put(`${API}/super-admin/outbound/campaigns/${editingCampaignId}`, payload);
      setShowBuilder(false);
      fetchCampaigns();
    } catch (err) {
      dialogs.error(err.response?.data?.detail || 'Failed to save changes');
    }
  };

  const handleDuplicate = async (c) => {
    const ok = await dialogs.confirm({
      title: 'Duplicate campaign?',
      message: `A new draft will be created from "${c.name}" with the latest contact list resolved from the same target.`,
      confirmText: 'Duplicate',
      variant: 'info',
    });
    if (!ok) return;
    try {
      await axios.post(`${API}/super-admin/outbound/campaigns/${c.id}/duplicate`);
      fetchCampaigns();
    } catch (err) {
      dialogs.error(err.response?.data?.detail || 'Failed to duplicate campaign');
    }
  };

  const handleStatusChange = async (id, status) => {
    await axios.put(`${API}/super-admin/outbound/campaigns/${id}/status`, { status });
    fetchCampaigns();
    if (selectedCampaign === id) viewDetail(id);
  };

  const handleDelete = async (id) => {
    const ok = await dialogs.confirm({
      title: 'Delete campaign?',
      message: 'The campaign and all its send records will be permanently removed. This cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    await axios.delete(`${API}/super-admin/outbound/campaigns/${id}`);
    if (selectedCampaign === id) { setSelectedCampaign(null); setCampaignDetail(null); }
    fetchCampaigns();
  };

  const viewDetail = async (id) => {
    setSelectedCampaign(id);
    try {
      const res = await axios.get(`${API}/super-admin/outbound/campaigns/${id}`);
      setCampaignDetail(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-600', scheduled: 'bg-blue-50 text-blue-700 border border-blue-200',
      active: 'bg-green-50 text-green-700 border border-green-200', paused: 'bg-amber-50 text-amber-700 border border-amber-200',
      completed: 'bg-purple-50 text-purple-700 border border-purple-200', cancelled: 'bg-red-50 text-red-700 border border-red-200',
    };
    return <span className={`px-2 py-0.5 text-xs font-medium rounded ${styles[status] || 'bg-gray-100 text-gray-600'}`}>{status}</span>;
  };

  const updateStep = (idx, field, value) => {
    setForm(prev => {
      const steps = [...prev.steps];
      steps[idx] = { ...steps[idx], [field]: value };
      return { ...prev, steps };
    });
  };

  return (
    <div className="space-y-4">
      {/* Campaign detail view */}
      {campaignDetail && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900">{campaignDetail.campaign.name}</h3>
              <p className="text-sm text-gray-500">{campaignDetail.total_contacts} contacts | Started {new Date(campaignDetail.campaign.start_date).toLocaleDateString('en-GB')}</p>
            </div>
            <div className="flex items-center gap-3">
              {getStatusBadge(campaignDetail.campaign.status)}
              <button onClick={() => { setSelectedCampaign(null); setCampaignDetail(null); }}
                className="text-sm text-gray-500 hover:text-gray-800">Close</button>
            </div>
          </div>

          {/* Step stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Object.entries(campaignDetail.step_stats).map(([step, stats]) => {
              const openRate = stats.sent > 0 ? Math.round((stats.opened / stats.sent) * 100) : 0;
              const clickRate = stats.sent > 0 ? Math.round((stats.clicked / stats.sent) * 100) : 0;
              return (
                <div key={step} className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Step {step}</h4>
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-600 font-medium">{stats.sent} sent</span>
                    {stats.failed > 0 && <span className="text-red-600 font-medium">{stats.failed} failed</span>}
                    <span className="text-gray-400">{campaignDetail.total_contacts - stats.total} pending</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{ width: `${(stats.sent / campaignDetail.total_contacts * 100)}%` }} />
                  </div>
                  {stats.sent > 0 && (
                    <div className="mt-3 space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">Opened</span>
                        <span className="font-medium text-blue-600">{openRate}% ({stats.opened}/{stats.sent})</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${openRate}%` }} />
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">Clicked</span>
                        <span className="font-medium text-amber-600">{clickRate}% ({stats.clicked}/{stats.sent})</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div className="bg-amber-500 h-1.5 rounded-full transition-all" style={{ width: `${clickRate}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Recent sends */}
          {campaignDetail.sends.length > 0 && (
            <div className="overflow-x-auto">
              <table data-testid="campaign-sends-table" className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Step</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Opened</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Clicked</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {campaignDetail.sends.slice(0, 50).map((s, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-600">{s.contact_email}</td>
                      <td className="px-3 py-2">{s.step_number}</td>
                      <td className="px-3 py-2 text-gray-600 truncate max-w-[200px]">{s.subject}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 text-xs rounded ${s.status === 'sent' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{s.status}</span>
                      </td>
                      <td className="px-3 py-2">
                        {s.opened_at ? (
                          <span className="px-2 py-0.5 text-xs rounded bg-blue-50 text-blue-700">Yes</span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {s.clicked_at ? (
                          <span className="px-2 py-0.5 text-xs rounded bg-amber-50 text-amber-700">Yes</span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{new Date(s.sent_at).toLocaleString('en-GB')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{campaigns.length} campaign(s)</p>
        <button data-testid="create-campaign-btn"
          onClick={openCreate}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors">
          New Campaign
        </button>
      </div>

      {/* Campaign builder modal */}
      {showBuilder && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" data-testid="campaign-builder-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {isEditMode ? `Edit Campaign — ${editingStatus}` : 'New Campaign'}
              </h3>
              {isEditMode && structuralLocked && (
                <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded">
                  Active — pause to edit steps, schedule, or recipients
                </span>
              )}
            </div>

            <div className="space-y-4">
              <input data-testid="campaign-name-input" placeholder="Campaign name" value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />

              <input placeholder="Sender name (appears in emails)" value={form.sender_name}
                onChange={e => setForm(p => ({ ...p, sender_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />

              {/* Steps */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-700">Email Steps</h4>
                {form.steps.map((step, idx) => (
                  <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg ${structuralLocked ? 'bg-gray-100 opacity-70' : 'bg-gray-50'}`}>
                    <span className="text-sm font-medium text-gray-600 w-16">Step {step.step_number}</span>
                    <select value={step.template_id} onChange={e => updateStep(idx, 'template_id', e.target.value)}
                      disabled={structuralLocked}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-500">
                      <option value="">Select template...</option>
                      {templates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.category?.replace('_', ' ')})</option>)}
                    </select>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-500">Day</span>
                      <input type="number" min="0" value={step.delay_days}
                        onChange={e => updateStep(idx, 'delay_days', parseInt(e.target.value) || 0)}
                        disabled={structuralLocked}
                        className="w-16 px-2 py-2 border border-gray-300 rounded-lg text-sm text-center disabled:bg-gray-100 disabled:text-gray-500" />
                    </div>
                  </div>
                ))}
              </div>

              {/* Recipients */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700">
                  Recipients
                  {recipientsLocked && <span className="ml-2 text-xs text-amber-700 font-normal">(locked — sends already started)</span>}
                </h4>
                <div className="flex gap-4">
                  <label className={`flex items-center gap-2 text-sm ${(structuralLocked || recipientsLocked) ? 'opacity-60' : ''}`}>
                    <input data-testid="recipients-all-radio" type="radio" checked={form.use_all_contacts}
                      onChange={() => setForm(p => ({ ...p, use_all_contacts: true, group_id: '' }))}
                      disabled={structuralLocked || recipientsLocked} />
                    All active contacts ({activeContactCount})
                  </label>
                  <label className={`flex items-center gap-2 text-sm ${(structuralLocked || recipientsLocked) ? 'opacity-60' : ''}`}>
                    <input data-testid="recipients-group-radio" type="radio" checked={!form.use_all_contacts}
                      onChange={() => setForm(p => ({ ...p, use_all_contacts: false }))}
                      disabled={structuralLocked || recipientsLocked} />
                    By group
                  </label>
                </div>
                {!form.use_all_contacts && (
                  groups.length === 0 ? (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                      No groups exist yet. Create one from the Contacts tab first.
                    </div>
                  ) : (
                    <select data-testid="campaign-group-select" value={form.group_id}
                      onChange={e => setForm(p => ({ ...p, group_id: e.target.value }))}
                      disabled={structuralLocked || recipientsLocked}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-500">
                      <option value="">Select group...</option>
                      {groups.map(g => (
                        <option key={g.id} value={g.id}>
                          {g.name} — {g.active_member_count} active member{g.active_member_count === 1 ? '' : 's'}
                        </option>
                      ))}
                    </select>
                  )
                )}
              </div>

              {/* Schedule */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700">Schedule</h4>
                <div className="flex items-center gap-3">
                  <input type="datetime-local" value={form.start_date}
                    onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))}
                    disabled={structuralLocked}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-500" />
                  <span className="text-xs text-gray-500">
                    {form.start_date
                      ? `Starts ${new Date(form.start_date).toLocaleString('en-GB', { timeZoneName: 'short' })}`
                      : 'Leave empty to start immediately'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowBuilder(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              {isEditMode ? (
                <>
                  <button data-testid="save-changes-btn" onClick={handleSaveChanges}
                    className="px-4 py-2 bg-white border border-purple-600 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-50">
                    Save Changes
                  </button>
                  {editingStatus === 'draft' && (
                    <button data-testid="launch-campaign-btn" onClick={handleLaunch}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
                      Save & Launch
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button data-testid="save-draft-btn" onClick={handleSaveDraft}
                    className="px-4 py-2 bg-white border border-purple-600 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-50">
                    Save as Draft
                  </button>
                  <button data-testid="launch-campaign-btn" onClick={handleLaunch}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
                    Launch Campaign
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Campaigns list */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" /></div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-12 text-gray-500 bg-white rounded-xl border border-gray-200">
            <p className="font-medium">No campaigns yet</p>
            <p className="text-sm mt-1">Create your first outbound marketing campaign</p>
          </div>
        ) : campaigns.map(c => (
          <div key={c.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h4 className="font-semibold text-gray-900">{c.name}</h4>
                {getStatusBadge(c.status)}
              </div>
              <div className="flex items-center gap-2">
                {c.status === 'scheduled' && (
                  <button data-testid={`start-now-${c.id}`} onClick={async () => {
                    const ok = await dialogs.confirm({
                      title: 'Start this campaign now?',
                      message: 'The first step will go out within the next processing cycle (up to 15 min).',
                      confirmText: 'Start now',
                      variant: 'success',
                    });
                    if (ok) handleStatusChange(c.id, 'active');
                  }} className="text-xs text-green-600 hover:text-green-800 font-medium px-2 py-1">Start Now</button>
                )}
                {c.status === 'draft' && (
                  <button data-testid={`launch-${c.id}`} onClick={async () => {
                    const ok = await dialogs.confirm({
                      title: 'Launch campaign?',
                      message: `"${c.name}" will go live and the first step will be queued. You can pause anytime.`,
                      confirmText: 'Launch',
                      variant: 'success',
                    });
                    if (ok) handleStatusChange(c.id, 'active');
                  }} className="text-xs text-green-600 hover:text-green-800 font-medium px-2 py-1">Launch</button>
                )}
                {c.status === 'active' && (
                  <button onClick={() => handleStatusChange(c.id, 'paused')} className="text-xs text-amber-600 hover:text-amber-800 font-medium px-2 py-1">Pause</button>
                )}
                {c.status === 'paused' && (
                  <button onClick={() => handleStatusChange(c.id, 'active')} className="text-xs text-green-600 hover:text-green-800 font-medium px-2 py-1">Resume</button>
                )}
                {['draft', 'active', 'paused', 'scheduled'].includes(c.status) && (
                  <button data-testid={`edit-${c.id}`} onClick={() => openEdit(c)} className="text-xs text-purple-600 hover:text-purple-800 font-medium px-2 py-1">Edit</button>
                )}
                {['active', 'paused', 'scheduled', 'draft'].includes(c.status) && (
                  <button onClick={() => handleStatusChange(c.id, 'cancelled')} className="text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1">Cancel</button>
                )}
                <button data-testid={`duplicate-${c.id}`} onClick={() => handleDuplicate(c)} className="text-xs text-gray-600 hover:text-gray-800 font-medium px-2 py-1">Duplicate</button>
                <button onClick={() => viewDetail(c.id)} className="text-xs text-purple-600 hover:text-purple-800 font-medium px-2 py-1">View</button>
                <button onClick={() => handleDelete(c.id)} className="text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1">Delete</button>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500 items-center">
              <span>{c.contact_count} contacts</span>
              <span>{c.steps?.length || 0} steps</span>
              {c.group_id && groupById[c.group_id] && (
                <span className="flex items-center gap-1">Targeting: <GroupBadge group={groupById[c.group_id]} /></span>
              )}
              <span>Started: {c.start_date ? new Date(c.start_date).toLocaleDateString('en-GB') : 'Not set'}</span>
              {c.tracking && c.tracking.sent > 0 && (
                <>
                  <span className="text-blue-600 font-medium">{Math.round((c.tracking.opened / c.tracking.sent) * 100)}% opened</span>
                  <span className="text-amber-600 font-medium">{Math.round((c.tracking.clicked / c.tracking.sent) * 100)}% clicked</span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      {dialogs.render()}
    </div>
  );
};


// ============================================================================
// INBOX TAB
// ============================================================================

const InboxTab = () => {
  const dialogs = useDialogs();
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadContact, setThreadContact] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);

  const fetchInbox = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 50 });
      if (unreadOnly) params.append('unread_only', 'true');
      const res = await axios.get(`${API}/super-admin/outbound/inbox?${params}`);
      setMessages(res.data.messages);
      setUnreadCount(res.data.unread_count);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error('Failed to fetch inbox:', err);
    } finally {
      setIsLoading(false);
    }
  }, [unreadOnly]);

  useEffect(() => { fetchInbox(1); }, [fetchInbox]);

  const openThread = async (contactEmail) => {
    setSelectedThread(contactEmail);
    try {
      const res = await axios.get(`${API}/super-admin/outbound/inbox/thread/${encodeURIComponent(contactEmail)}`);
      setThreadMessages(res.data.messages);
      setThreadContact(res.data.contact);
      fetchInbox(pagination.page);
    } catch (err) {
      console.error('Failed to load thread:', err);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedThread) return;
    setIsSending(true);
    try {
      const lastInbound = [...threadMessages].reverse().find(m => m.direction === 'inbound');
      const subject = lastInbound?.subject?.startsWith('Re:')
        ? lastInbound.subject
        : `Re: ${lastInbound?.subject || 'Your enquiry'}`;

      await axios.post(`${API}/super-admin/outbound/inbox/reply`, {
        to_email: selectedThread,
        to_name: threadContact?.name || '',
        subject,
        body_html: `<div style="font-family: Arial, sans-serif; line-height: 1.6;">${replyText.replace(/\n/g, '<br>')}</div>`,
        body_plain: replyText,
        in_reply_to: lastInbound?.message_id || '',
        references: lastInbound?.references || lastInbound?.message_id || '',
      });
      setReplyText('');
      openThread(selectedThread);
    } catch (err) {
      dialogs.error('Failed to send reply');
    } finally {
      setIsSending(false);
    }
  };

  // Group messages by contact for the list view
  const grouped = {};
  messages.forEach(m => {
    if (!grouped[m.contact_email]) {
      grouped[m.contact_email] = { ...m, count: 1 };
    } else {
      grouped[m.contact_email].count++;
    }
  });
  const uniqueThreads = Object.values(grouped);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500">Replies to your outreach emails</p>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded-full">{unreadCount} unread</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={unreadOnly} onChange={e => setUnreadOnly(e.target.checked)} className="rounded" />
            Unread only
          </label>
          <button onClick={() => fetchInbox(1)} className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Refresh</button>
        </div>
      </div>

      <div className="flex gap-4 h-[600px]">
        {/* Thread list */}
        <div className="w-1/3 bg-white rounded-xl shadow-sm border border-gray-200 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" /></div>
          ) : uniqueThreads.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="font-medium">No messages yet</p>
              <p className="text-sm mt-1">Replies to your campaigns will appear here</p>
            </div>
          ) : uniqueThreads.map(m => (
            <button key={m.contact_email} data-testid={`inbox-thread-${m.contact_email}`}
              onClick={() => openThread(m.contact_email)}
              className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                selectedThread === m.contact_email ? 'bg-purple-50 border-l-2 border-l-purple-600' : ''
              }`}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm text-gray-900 truncate">
                  {m.contact_name || m.contact_email}
                </span>
                {!m.read && m.direction === 'inbound' && (
                  <span className="w-2 h-2 rounded-full bg-purple-600 flex-shrink-0" />
                )}
              </div>
              {m.contact_company && <p className="text-xs text-gray-500 truncate">{m.contact_company}</p>}
              <p className="text-xs text-gray-400 truncate mt-0.5">{m.subject}</p>
              <p className="text-xs text-gray-400 mt-0.5">{new Date(m.received_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
            </button>
          ))}
        </div>

        {/* Thread detail + reply */}
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col overflow-hidden">
          {!selectedThread ? (
            <div className="flex items-center justify-center h-full text-gray-400">
              <p>Select a conversation to view</p>
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h4 className="font-semibold text-gray-900">{threadContact?.name || selectedThread}</h4>
                <p className="text-xs text-gray-500">{selectedThread} {threadContact?.company ? `| ${threadContact.company}` : ''}</p>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {threadMessages.map((m, i) => (
                  <div key={i} className={`flex ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-xl px-4 py-3 ${
                      m.direction === 'outbound'
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      <p className="text-xs font-medium mb-1 opacity-70">
                        {m.direction === 'outbound' ? 'You' : (m.contact_name || m.contact_email)} | {new Date(m.received_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <p className="text-xs opacity-70 mb-2">{m.subject}</p>
                      {m.body_plain ? (
                        <p className="text-sm whitespace-pre-wrap">{m.body_plain}</p>
                      ) : (
                        <div className="text-sm" dangerouslySetInnerHTML={{ __html: sanitiseInboundEmailHtml(m.body_html) }} />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply composer */}
              <div className="border-t border-gray-200 p-4">
                <textarea
                  data-testid="inbox-reply-input"
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  placeholder="Type your reply..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <div className="flex justify-end mt-2">
                  <button
                    data-testid="send-reply-btn"
                    onClick={sendReply}
                    disabled={isSending || !replyText.trim()}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-40"
                  >
                    {isSending ? 'Sending...' : 'Send Reply'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {dialogs.render()}
    </div>
  );
};


// ============================================================================
// LEAD SOURCES TAB — scrape public directories, split generic/personal emails
// ============================================================================

const LeadSourcesTab = () => {
  const dialogs = useDialogs();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/super-admin/leads/scrape/jobs`);
      setJobs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load scrape jobs', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Poll every 5 s while any job is running.
  useEffect(() => {
    if (!jobs.some(j => j.status === 'running')) return undefined;
    const t = setInterval(loadJobs, 5000);
    return () => clearInterval(t);
  }, [jobs, loadJobs]);

  const handleStart = async () => {
    const confirmed = await dialogs.confirm({
      title: 'Start lead scrape?',
      message:
        'This will scrape the Construction London directory (~12,000 listings, ~5–8 hours). You can stop it at any time and download partial results.',
      confirmLabel: 'Start scrape',
    });
    if (!confirmed) return;
    setStarting(true);
    try {
      await axios.post(`${API}/super-admin/leads/scrape/start`, { source: 'construction_london' });
      await loadJobs();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      await dialogs.alert({ title: 'Could not start scrape', message: detail, variant: 'danger' });
    } finally {
      setStarting(false);
    }
  };

  const handleStop = async (jobId) => {
    const ok = await dialogs.confirm({
      title: 'Stop running scrape?',
      message: 'The job will halt at the next safe checkpoint. Partial results are saved and downloadable.',
      confirmLabel: 'Stop scrape',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await axios.post(`${API}/super-admin/leads/scrape/jobs/${jobId}/stop`);
      await loadJobs();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      await dialogs.alert({ title: 'Could not stop job', message: detail, variant: 'danger' });
    }
  };

  const handleResume = async (jobId) => {
    try {
      await axios.post(`${API}/super-admin/leads/scrape/jobs/${jobId}/resume`);
      await loadJobs();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      await dialogs.alert({ title: 'Could not resume job', message: detail, variant: 'danger' });
    }
  };

  // Treat a "running" job as stalled if it hasn't updated its progress
  // doc in over 2 minutes — likely a worker died before auto-resume.
  const isStalled = (j) => {
    if (j.status !== 'running') return false;
    if (!j.updated_at) return false;
    const ageMs = Date.now() - new Date(j.updated_at).getTime();
    return ageMs > 120000;
  };

  const handleDelete = async (jobId) => {
    const ok = await dialogs.confirm({
      title: 'Delete this scrape job?',
      message: 'All scraped leads from this job will be permanently removed. This cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/super-admin/leads/scrape/jobs/${jobId}`);
      await loadJobs();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      await dialogs.alert({ title: 'Could not delete', message: detail, variant: 'danger' });
    }
  };

  const downloadCsv = (jobId, emailType) => {
    // Trigger a download via a hidden anchor — axios body-streaming + blob is overkill here.
    const url = `${API}/super-admin/leads/scrape/jobs/${jobId}/download?email_type=${emailType}`;
    window.open(url, '_blank');
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const runningJob = jobs.find(j => j.status === 'running');

  return (
    <div data-testid="lead-sources-tab" className="space-y-6">
      {/* Source card */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Construction London Directory</h3>
            <p className="text-sm text-gray-600 mt-1">
              Public B2B directory at <span className="font-mono text-xs">constructionlondon.co.uk</span> — ~12,000 listings across
              builders, electricians, plumbers, heating engineers, joiners and more. The scraper extracts business name, contact,
              email, website, address and postcode, then splits emails into <span className="font-medium">generic</span> (info@, hello@, sales@)
              and <span className="font-medium">personal</span> (named-person) buckets.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Expected runtime: 5–8 hours at 1.5 s per request. Safe to leave running; stop and download partial results anytime.
            </p>
          </div>
          <button
            onClick={handleStart}
            disabled={starting || !!runningJob}
            className="px-4 py-2 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm whitespace-nowrap"
            data-testid="lead-scrape-start-btn"
          >
            {runningJob ? 'Job running…' : starting ? 'Starting…' : 'Start scrape'}
          </button>
        </div>
      </div>

      {/* Jobs table */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Recent scrape jobs</h3>
          <button onClick={loadJobs} className="text-xs text-purple-600 hover:text-purple-800">Refresh</button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : jobs.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">No scrape jobs yet. Click "Start scrape" above to begin.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Source</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Status</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Progress</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Leads</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Started</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Finished</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {jobs.map((j) => {
                  const p = j.progress || {};
                  const pct = p.categories_total
                    ? Math.round((p.categories_done / p.categories_total) * 100)
                    : 0;
                  const statusColour = {
                    running: 'bg-blue-50 text-blue-700 border-blue-200',
                    completed: 'bg-green-50 text-green-700 border-green-200',
                    stopped: 'bg-amber-50 text-amber-700 border-amber-200',
                    failed: 'bg-red-50 text-red-700 border-red-200',
                  }[j.status] || 'bg-gray-50 text-gray-700 border-gray-200';
                  return (
                    <tr key={j.id} data-testid={`scrape-job-${j.id}`}>
                      <td className="px-4 py-2.5 text-gray-900">{j.source_label || j.source}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded border ${statusColour}`}>
                          {j.status}
                        </span>
                        {isStalled(j) && (
                          <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded border bg-amber-50 text-amber-700 border-amber-200">
                            stalled
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-gray-700">
                        {p.categories_total ? (
                          <div className="min-w-[160px]">
                            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                              <span>{p.categories_done}/{p.categories_total} categories</span>
                              <span>{pct}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div className="bg-purple-600 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                            {p.current_category && j.status === 'running' && (
                              <p className="text-xs text-gray-500 mt-1 truncate">→ {p.current_category}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-gray-700">
                        <div className="text-xs">
                          <div><span className="font-medium">{p.leads_saved || 0}</span> total</div>
                          <div className="text-gray-500">
                            {p.generic_count || 0} generic · {p.personal_count || 0} personal
                          </div>
                          {p.errors > 0 && (
                            <div className="text-amber-600">{p.errors} error{p.errors === 1 ? '' : 's'}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 text-xs">{formatDate(j.started_at)}</td>
                      <td className="px-4 py-2.5 text-gray-600 text-xs">{formatDate(j.completed_at)}</td>
                      <td className="px-4 py-2.5 text-right space-x-2 whitespace-nowrap">
                        {(p.generic_count || 0) > 0 && (
                          <button
                            onClick={() => downloadCsv(j.id, 'generic')}
                            className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                            data-testid={`download-generic-${j.id}`}
                          >
                            Generic CSV
                          </button>
                        )}
                        {(p.personal_count || 0) > 0 && (
                          <button
                            onClick={() => downloadCsv(j.id, 'personal')}
                            className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                            data-testid={`download-personal-${j.id}`}
                          >
                            Personal CSV
                          </button>
                        )}
                        {j.status === 'running' && (
                          <button
                            onClick={() => handleStop(j.id)}
                            className="text-xs text-amber-600 hover:text-amber-800 font-medium"
                            data-testid={`stop-scrape-${j.id}`}
                          >
                            Stop
                          </button>
                        )}
                        {(isStalled(j) || j.status === 'stopped' || j.status === 'failed') && (
                          <button
                            onClick={() => handleResume(j.id)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            data-testid={`resume-scrape-${j.id}`}
                          >
                            Resume
                          </button>
                        )}
                        {j.status !== 'running' && (
                          <button
                            onClick={() => handleDelete(j.id)}
                            className="text-xs text-red-500 hover:text-red-700 font-medium"
                            data-testid={`delete-scrape-${j.id}`}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};


// ============================================================================
// MAIN PAGE
// ============================================================================

const MarketingCampaignsPage = () => {
  const { currentUser } = useOutletContext();
  const [activeTab, setActiveTab] = useState('campaigns');

  return (
    <div data-testid="marketing-campaigns-page" className="space-y-6">
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h1 className="text-3xl font-bold">Marketing Campaigns</h1>
            <p className="text-purple-200 text-sm">Manage outbound email campaigns to potential businesses</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6" data-testid="campaign-tabs">
          {TABS.map(tab => (
            <button key={tab.key} data-testid={`tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === tab.key
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'contacts' && <ContactsTab />}
      {activeTab === 'templates' && <TemplatesTab />}
      {activeTab === 'campaigns' && <CampaignsTab />}
      {activeTab === 'inbox' && <InboxTab />}
      {activeTab === 'lead-sources' && <LeadSourcesTab />}
    </div>
  );
};

export default MarketingCampaignsPage;
